<script lang="ts">
  import { goto } from '$app/navigation';
  import Button from '../Button.svelte';
  import Modal from '../Modal.svelte';
  import RichInput from '../richinput/RichInput.svelte';
  import SlideOver from '../SlideOver.svelte';
  import { defaults, setMessage, superForm } from 'sveltekit-superforms';
  import type { Editor } from '@tiptap/core';
  import type { IAnnouncement } from '$utils/interfaces/admin';
  import { sideNavOpen } from '$stores';
  import { announcementSchema } from '$utils/schemas/announcements';
  import { ContentType } from '$shared/lib';
  import { zod } from 'sveltekit-superforms/adapters';
  import { page } from '$app/stores';
  import { client } from '$lib/api/Client';
  import { toastCustom } from '$utils/toasts';

  export let showAnnouncementCreateModal = false;
  export let isEditing = false;
  export let announcement: Partial<IAnnouncement> = {};

  let modalEditor: Editor;
  let slideOverEditor: Editor;
  const {
    form: announcementForm,
    errors: announcementErrors,
    constraints: announcementConstraints,
    enhance: announcementEnhance,
    delayed: announcementDelayed,
  } = superForm(defaults(zod(announcementSchema)), {
    SPA: true,
    validators: zod(announcementSchema),
    taintedMessage: false,
    async onUpdate({ form }) {
      if (!form.valid) {
        return toastCustom({
          negative: true,
          title:
            'Oops! It seems there are errors in your input. Please review and correct them to proceed.',
          position: 'bottom-center',
        });
      }
      const { body } = form.data;
      const announcementId = $page.url.searchParams.get('announcementId');
      if (announcementId) {
        await client.admin.updateAnnouncement(Number(announcementId), body);
      } else {
        await client.admin.createAnnouncement(body);
      }
      return toastCustom({
        positive: true,
        title: announcementId ? 'ANNOUNCEMENT EDITED' : 'ANNOUNCEMENT CREATED',
        position: 'bottom-center',
      });
    },
    onResult({ result }) {
      setTimeout(async () => {
        if (result.type === 'success') {
          showAnnouncementCreateModal = false;
          $sideNavOpen = false;
          await goto('/posts', {
            invalidateAll: true,
          });
        }
      }, 100);
    },
  });

  $: {
    const body =
      typeof announcement.body === 'object'
        ? JSON.stringify(announcement.body)
        : announcement.body || '';
    $announcementForm = { body };
  }
  const reset = () => {
    announcement = {};
    modalEditor?.commands.clearContent();
    slideOverEditor?.commands.clearContent();
    showAnnouncementCreateModal = false;
  };
  $: disabled =
    !$announcementForm.body ||
    $announcementDelayed ||
    (modalEditor?.isEmpty && slideOverEditor?.isEmpty);
</script>

<!-- Desktop version -->
<Modal
  bind:modalOpen={showAnnouncementCreateModal}
  title={isEditing ? 'Edit announcement' : 'Create announcement'}
  size="md"
  class="hidden md:block"
>
  <p class="font-bold">**** This will be delivered to all users on the platform. ****</p>
  <form
    action="/announcements?/announce{isEditing ? `&announcementId=${announcement.id}` : ''}"
    method="post"
    enctype="multipart/form-data"
    use:announcementEnhance
    class="gap-y-lg mt-3 flex flex-col"
  >
    <RichInput
      contentType={ContentType.ANNOUNCEMENT}
      label="Write your announcement"
      bind:editor={modalEditor}
      bind:value={$announcementForm.body}
      name="body"
      {...$announcementConstraints.body}
      bind:errors={$announcementErrors.body}
    />
    <div class="gap-x-lg flex items-center">
      <Button
        tertiary
        size="md"
        class="flex-1"
        on:click={() => (isEditing ? (showAnnouncementCreateModal = false) : reset())}
        >Cancel</Button
      >
      <Button
        type="submit"
        secondary
        size="md"
        submitting={$announcementDelayed}
        {disabled}
        class="flex-1"
      >
        Save
        {#if isEditing}
          <span>changes</span>
        {/if}
      </Button>
    </div>
  </form>
</Modal>

<!-- Mobile version -->
<SlideOver
  bind:slideOverOpen={showAnnouncementCreateModal}
  title={isEditing ? 'Edit announcement' : 'Create announcement'}
  class="block !h-screen md:hidden"
>
  <form
    action="/announcements?/announce{isEditing ? `&announcementId=${announcement.id}` : ''}"
    method="post"
    enctype="multipart/form-data"
    use:announcementEnhance
    class="gap-y-lg flex flex-col"
  >
    <RichInput
      contentType={ContentType.ANNOUNCEMENT}
      label="Write your announcement"
      bind:editor={slideOverEditor}
      bind:value={$announcementForm.body}
      name="body"
      {...$announcementConstraints.body}
      bind:errors={$announcementErrors.body}
    />
    <div class="gap-x-lg flex items-center">
      <Button tertiary size="md" class="flex-1" on:click={reset}>Cancel</Button>
      <Button
        type="submit"
        secondary
        size="md"
        submitting={$announcementDelayed}
        {disabled}
        class="flex-1"
      >
        Save
        {#if isEditing}
          <span>changes</span>
        {/if}
      </Button>
    </div>
  </form>
</SlideOver>
